/**
 * BeamMaterial — laser / energy-beam surface for cylinders or camera-facing
 * ribbon quads: a hot core with a soft glow falloff across the beam width,
 * UV-scrolling energy noise along the length, and end fading.
 *
 * UV convention: `uv.x` runs ALONG the beam (0 = start, 1 = end), `uv.y`
 * across it (0.5 = centre line) — three's CylinderGeometry unwraps this way
 * when the cylinder's height axis is the beam axis.
 */

import * as THREE from 'three/webgpu';
import {
  Fn, uniform, float, vec3, vec4, uv, pow, abs, sin, time, saturate, smoothstep,
} from 'three/tsl';
import { markMrtOverlay } from '../render/post/mrtOverlay.js';

export interface BeamConfig {
  /** Core colour (default white-hot 1.5,1.5,1.5). */
  coreColor?: [number, number, number];
  /** Glow colour (default cyan 0.2,0.7,1). */
  glowColor?: [number, number, number];
  /** Core sharpness exponent (default 6). */
  corePower?: number;
  /** Overall intensity (default 2). */
  intensity?: number;
  /** Energy scroll speed along the beam (default 3). */
  scrollSpeed?: number;
  /** Length-wise ripple frequency (default 12). */
  rippleFrequency?: number;
  /** Fraction of the length faded at each end (default 0.08). */
  endFade?: number;
}

export interface BeamUniforms {
  coreColor: { value: THREE.Vector3 };
  glowColor: { value: THREE.Vector3 };
  corePower: { value: number };
  intensity: { value: number };
  scrollSpeed: { value: number };
  rippleFrequency: { value: number };
  endFade: { value: number };
}

/** CPU mirror of the cross-beam profile (exported for tests). */
export function beamProfileCpu(v: number, corePower: number): { core: number; glow: number } {
  const d = Math.abs(v * 2 - 1); // 0 at centre, 1 at edges
  const core = Math.pow(Math.max(1 - d, 0), corePower);
  const glow = Math.max(1 - d, 0);
  return { core, glow };
}

export function createBeamMaterial(config?: BeamConfig): {
  material: THREE.NodeMaterial; uniforms: BeamUniforms;
} {
  const cc = config?.coreColor ?? [1.5, 1.5, 1.5];
  const gc = config?.glowColor ?? [0.2, 0.7, 1];
  const uCore = uniform(vec3(cc[0], cc[1], cc[2]));
  const uGlow = uniform(vec3(gc[0], gc[1], gc[2]));
  const uCorePower = uniform(config?.corePower ?? 6);
  const uIntensity = uniform(config?.intensity ?? 2);
  const uScroll = uniform(config?.scrollSpeed ?? 3);
  const uRipple = uniform(config?.rippleFrequency ?? 12);
  const uEndFade = uniform(config?.endFade ?? 0.08);

  const material = new THREE.MeshBasicNodeMaterial();
  material.transparent = true;
  material.depthWrite = false;
  material.side = THREE.DoubleSide;
  material.blending = THREE.AdditiveBlending;
  // WHY marked: an overlay drawn over a surface it does not replace must not rewrite that surface's MRT normal /
  // velocity / metalness / roughness (THE MRT OVERLAY LAW) — unmarked, GTAO and SSR shaded the wall behind it as the
  // overlay's own normal (Atlas's ride shield, ride haze and civic holograms were all drawn unmarked).
  markMrtOverlay(material);

  material.colorNode = Fn(() => {
    const u0 = uv();
    const d = abs(u0.y.mul(2).sub(1));
    const across = saturate(float(1).sub(d));
    const core = pow(across, uCorePower);
    // Scrolling energy ripple along the length.
    const ripple = sin(u0.x.mul(uRipple).sub(time.mul(uScroll)))
      .mul(0.5).add(0.5).mul(0.3).add(0.7);
    // End fades.
    const ends = smoothstep(float(0), uEndFade, u0.x)
      .mul(smoothstep(float(0), uEndFade, float(1).sub(u0.x)));
    const rgb = uGlow.mul(across).add(uCore.mul(core)).mul(ripple).mul(uIntensity).mul(ends);
    const alpha = saturate(across.mul(ripple).mul(ends));
    return vec4(rgb, alpha);
  })();

  return {
    material: material as THREE.NodeMaterial,
    uniforms: {
      coreColor: uCore as unknown as { value: THREE.Vector3 },
      glowColor: uGlow as unknown as { value: THREE.Vector3 },
      corePower: uCorePower as unknown as { value: number },
      intensity: uIntensity as unknown as { value: number },
      scrollSpeed: uScroll as unknown as { value: number },
      rippleFrequency: uRipple as unknown as { value: number },
      endFade: uEndFade as unknown as { value: number },
    },
  };
}
